import React, { useMemo, useState, useEffect } from "react";

import { useStateManagerContext } from "~/context/stateManager";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useNeighborhoodMap } from "~/sdks/NeighborhoodMap/context";
import { useTracking } from "@local-logic/core/tracking";

import { FeatureCollection, MultiPolygon } from "@turf/turf";
import { mapUtils, typeguardUtils } from "@local-logic/core/utils";
import errorTracker from "~/utils/errorTracker";

import * as Map from "~/sdks/NeighborhoodMap/components/Map";
import { SDKProps } from "~/types";

import { categories } from "~/sdks/NeighborhoodMap/constants";

import {
  type POICategory,
  type Marker,
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "~/sdks/NeighborhoodMap/types";
import * as Filters from "../Filters";
import { RadiusDropdown } from "../RadiusDropdown";

import * as styles from "./styles";

export const Content = () => {
  const { sdkState } = useStateManagerContext<SDKProps>();
  const flags = useFlags();
  const tracking = useTracking();
  const [mapMarkers, setMapMarkers] = useState<typeof markers>();
  const [schoolGeometry, setSchoolGeometry] =
    useState<FeatureCollection<MultiPolygon>>();

  const {
    isLoading,
    activePoiCategory,
    locationInfo,
    onSelectPoiCategory,
    neighborhoodGeometry,
    markers,
  } = useNeighborhoodMap();
  const [activeMarker, setActiveMarker] = useState<Marker | null>(null);
  const [dialogSelectedPoiCategory, setDialogSelectedPoiCategory] = useState<
    POICategory | undefined
  >();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setMapMarkers(markers);
  }, [markers, isLoading]);

  useEffect(() => {
    if (activeMarker) {
      setActiveMarker(null);
    }

    setDialogSelectedPoiCategory(activePoiCategory);
  }, [activePoiCategory]);

  const poiCategories = useMemo(
    () =>
      Object.entries(categories)
        .filter(
          ([category]) =>
            category !== "daycares" || flags.neighborhoodWrapDaycaresEnabled
        )
        .map(([category, value]) => ({
          key: category as POICategory,
          icon: value.icon,
        })),
    [flags]
  );

  const mapBounds = useMemo(
    () =>
      mapMarkers
        ? mapMarkers?.map((marker) => ({ lat: marker.lat, lng: marker.lng }))
        : undefined,
    [mapMarkers]
  );

  const handleOnSelectCategory = (category: POICategory) => {
    onSelectPoiCategory(category);

    tracking.postEvent({
      eventType: TrackingPipelineEvents.CATEGORY_CLICKED,
      eventAttributes: [
        {
          label: "category",
          data: category,
        },
      ],
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.CATEGORY_CLICKED,
      eventAttributes: {
        ll_category: category,
      },
    });
  };

  const handleOnDialogSelectCategory = (category: POICategory) => {
    setDialogSelectedPoiCategory(
      dialogSelectedPoiCategory === category ? undefined : category
    );
  };

  const handleMarkerClick = (marker: Marker) => () => {
    setActiveMarker(marker);

    errorTracker.addBreadCrumb("Content", "handleMarkerClick", {
      marker,
    });

    const activePoiType = "school_attributes" in marker ? "school" : "poi";

    if (
      typeguardUtils.poi.isSchool(marker, activePoiType) &&
      marker?.catchments &&
      Object.keys(marker?.catchments || []).length > 0
    ) {
      setSchoolGeometry(mapUtils.formatGeometry(marker.catchments[0].geometry));
    } else {
      setSchoolGeometry(undefined);
    }

    tracking.postEvent({
      eventType: TrackingPipelineEvents.POI_CLICKED,
      eventAttributes: [
        {
          label: "category",
          data: activePoiCategory,
        },
      ],
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.POI_CLICKED,
      eventAttributes: {
        ll_category: activePoiCategory,
      },
    });
  };

  const handleMarkerLinkClick = (url: string) => {
    tracking.postEvent({
      eventType: TrackingPipelineEvents.POI_LINK_CLICKED,
      eventAttributes: [
        {
          label: "category",
          data: activePoiCategory,
        },
        {
          label: "clicked_url",
          data: url,
        },
      ],
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.POI_LINK_CLICKED,
      eventAttributes: {
        ll_category: activePoiCategory,
        ll_clicked_url: url,
      },
    });
  };

  const handleMarkerPopupClose = () => {
    setActiveMarker(null);
    setSchoolGeometry(undefined);
  };

  const handleResetFilters = () => {
    onSelectPoiCategory(undefined);
  };

  const handleApplyFilter = () => {
    onSelectPoiCategory(dialogSelectedPoiCategory);
  };

  return (
    <>
      <Filters.Root>
        <div className={styles.header}>
          <Filters.ScrollList>
            {poiCategories.map((poiCategory) => (
              <Filters.Category
                key={`scroll-list-${poiCategory.key}`}
                category={poiCategory.key}
                icon={poiCategory.icon}
                activeCategory={activePoiCategory}
                onSelectCategory={handleOnSelectCategory}
              />
            ))}
          </Filters.ScrollList>
          <Filters.AllFiltersButton />
        </div>

        <Filters.Dialog
          onReset={handleResetFilters}
          onApply={handleApplyFilter}
        >
          {poiCategories.map((poiCategory) => (
            <Filters.Category
              key={`dialog-scroll-list-${poiCategory.key}`}
              category={poiCategory.key}
              icon={poiCategory.icon}
              activeCategory={dialogSelectedPoiCategory}
              onSelectCategory={handleOnDialogSelectCategory}
            />
          ))}
        </Filters.Dialog>

        <Map.Root
          options={{
            ...sdkState.options,
            lat: locationInfo?.coordinates.lat,
            lng: locationInfo?.coordinates.lng,
            theme: sdkState.globalOptions?.appearance?.theme,
            geometries: [
              {
                key: "neighborhood",
                geoJson: neighborhoodGeometry,
                color: getComputedStyle(
                  document.documentElement
                ).getPropertyValue("--ll-color-primary"),
              },
              {
                key: "school",
                geoJson: schoolGeometry,
                color: "#9333ea",
              },
            ],
          }}
        >
          <Map.BaseMap bounds={mapBounds}>
            <>
              <div className={styles.radiusSelector}>
                <RadiusDropdown />
              </div>

              {mapMarkers?.map((marker, index) => (
                <Map.Marker
                  key={`poi-marker-${marker.address}-${index}`}
                  lat={marker.lat}
                  lng={marker.lng}
                  icon={activePoiCategory && categories[activePoiCategory].icon}
                  onClick={handleMarkerClick(marker)}
                />
              ))}

              <Map.Popup
                marker={activeMarker}
                icon={activePoiCategory && categories[activePoiCategory].icon}
                onClose={handleMarkerPopupClose}
                onLinkClick={handleMarkerLinkClick}
              />
            </>
          </Map.BaseMap>
        </Map.Root>
      </Filters.Root>
    </>
  );
};
